import { t, Trans } from '@lingui/macro'
import { Form, Formik, FormikValues } from 'formik'
import { decamelizeKeys } from 'humps'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { bool, object, string } from 'yup'

import { Alert, Button, Checkbox, Heading, Input, Space } from '@/components'
import MutationErrors from '@/compositions/MutationErrors'
import { ErrorMessages, Routes, Storage } from '@/constants'
import { useApplyToCohortMutation, UserError } from '@/graphql/generated'
import useAuth from '@/hooks/useAuth'
import { post } from '@/rest/api'

import { ComponentTypes } from '../types'

enum ApplyingMode {
  SIGN_UP = 'SIGN_UP',
  SIGN_IN = 'SIGN_IN',
}

const Account: React.FC<React.PropsWithChildren<ComponentTypes>> = () => {
  const { query, replace } = useRouter()
  const [applyToCohort] = useApplyToCohortMutation()
  const [applyingMode, setApplyingMode] = useState(ApplyingMode.SIGN_UP)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [mutationErrors, setMutationErrors] = useState<UserError[]>()
  const { login } = useAuth()

  const isSigningUp = applyingMode === ApplyingMode.SIGN_UP

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    setIsSubmitting(true)

    // 1. Create / Login user
    // 2. Take data from `localStorage`
    // 3. Mutate to BE
    const { email, password, acceptedNewsletterOptIn } = values
    const profileData = localStorage.getItem(Storage.APPLY_USER_DATA)
    const callDetails = {
      [ApplyingMode.SIGN_UP]: {
        formData: {
          email,
          password,
          accepted_newsletter_opt_in: acceptedNewsletterOptIn,
          ...(profileData ? { ...decamelizeKeys(JSON.parse(profileData)) } : null),
        },
        endpoint: `/auth/signup`,
      },
      [ApplyingMode.SIGN_IN]: {
        formData: {
          email,
          password,
        },
        endpoint: `/auth/signin`,
      },
    }
    const { formData, endpoint } = callDetails[applyingMode]

    try {
      const { data } = await post(endpoint, {
        user: {
          ...formData,
        },
      })

      login(data.token)
    } catch (e) {
      setIsSubmitting(false)

      setMutationErrors([
        {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          message: (
            <>
              <Trans>
                Failed to register. Maybe you{' '}
                <button className="underline" onClick={() => setApplyingMode(ApplyingMode.SIGN_IN)}>
                  already have an account
                </button>
                ?
              </Trans>
            </>
          ),
        },
      ])

      return
    }

    const localStorageData = localStorage.getItem(Storage.APPLY_APPLICATION_DATA)

    if (localStorageData) {
      const { data } = await applyToCohort({
        variables: { ...JSON.parse(localStorageData) },
      })

      if (data?.applyToCohort?.errors) {
        return setMutationErrors(data?.applyToCohort?.errors)
      }

      // Remove `localStorage` data related with Profile and Registration steps.
      localStorage.removeItem(Storage.APPLY_USER_DATA)
      localStorage.removeItem(Storage.APPLY_APPLICATION_DATA)

      const applicantId = data?.applyToCohort?.applicant?.id

      replace(`/${query.slug}${Routes.SUCCESSFUL_APPLY}?applicant=${applicantId}`)
    }

    return
  }

  useEffect(() => setMutationErrors(undefined), [applyingMode])

  return (
    <>
      <MutationErrors errors={mutationErrors} />

      <Formik
        initialValues={{
          email: undefined,
          password: undefined,
          terms: false,
          acceptedNewsletterOptIn: true,
          creatorReachOutOptInText: false,
        }}
        validationSchema={object().shape({
          email: string().email(ErrorMessages.INVALID_EMAIL).required(ErrorMessages.REQUIRED_FIELD),
          password: string().required(ErrorMessages.REQUIRED_FIELD),
          terms: bool().oneOf([true], ErrorMessages.MUST_BE_CHECKED).required(),
        })}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <Space y={8}>
              <Heading level={2} variant={TextStyle => TextStyle.MEDIUM_LG_NORMAL}>
                {isSigningUp ? t`Create your account` : t`Sign in to your account`}
              </Heading>

              <Input name="email" label={t`Email address`} />
              <Input name="password" label={t`Password`} type="password" />

              <Alert type="info" size="md">
                {isSigningUp
                  ? t`Creating an account allows you to keep track of your application and apply to other Learning Communities with ease.`
                  : t`Logging in into your account allows you to keep track of your application and apply to other Learning Communities with ease.`}
              </Alert>

              <div>
                <Checkbox
                  name="acceptedNewsletterOptIn"
                  label={t`I would like to receive email updates with new courses, offers and tips`}
                />

                <Checkbox
                  name="terms"
                  label={
                    <>
                      <Trans>
                        I have read and accept the{' '}
                        <Link
                          href="https://www.coleap.com/terms"
                          target="_blank"
                          rel="noreferrer"
                          className="underline"
                        >
                          Terms & Conditions
                        </Link>
                      </Trans>
                    </>
                  }
                />
              </div>

              <Button type="submit" variant="primary" disabled={isSubmitting} loading={isSubmitting} block>
                {isSubmitting ? t`Please wait ...` : isSigningUp ? t`Register` : t`Sign in`}
              </Button>
            </Space>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default Account
